import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import logolight from "../../assets/images/logo-light.png";
import logodark from "../../assets/images/logo-dark.png";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, socialLogin } from "../../store/actions";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setErrorMessage("");
        
        // Dispatch the login action
        dispatch(loginUser(values, navigate));
      } catch (error) {
        console.error("Login error:", error);
        setLoading(false);
        
        if (error.response) {
          const { data, status } = error.response;
          
          // Handle different error statuses
          if (status === 400) {
            setErrorMessage("Invalid email or password. Please try again.");
          } else if (status === 403) {
            setErrorMessage("This account has been disabled. Please contact an admin.");
          } else {
            setErrorMessage(data.message || data.error || "An error occurred during login");
          }
        } else if (error.request) {
          setErrorMessage("Unable to connect to the server. Please check your internet connection.");
        } else {
          setErrorMessage("An unexpected error occurred. Please try again.");
        }
      }
    },
  });

  const error = useSelector((state) => state.login.error);

  const signIn = (type) => {
    dispatch(socialLogin(type, navigate));
  };

  useEffect(() => {
    document.body.className = "bg-pattern";
    return () => {
      document.body.className = "";
    };
  }, []);

  useEffect(() => {
    if (error) {
      setLoading(false);
      
      if (error.includes("403") || error.includes("disabled")) {
        setErrorMessage("This account has been disabled. Please contact an admin.");
      } else if (error.includes("400") || error.includes("Invalid")) {
        setErrorMessage("Invalid email or password. Please try again.");
      } else if (error.includes("network") || error.includes("Network")) {
        setErrorMessage("Unable to connect to the server. Please check your internet connection.");
      } else {
        setErrorMessage(error);
      }
    }
  }, [error]);

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card>
                <CardBody className="p-4">
                  <div>
                    <div className="text-center">
                      <Link to="/">
                        <img
                          src={logodark}
                          alt="dark logo"
                          height="100"
                          className="auth-logo logo-dark mx-auto"
                        />
                        <img
                          src={logodark}
                          alt="light logo"
                          height="100"
                          className="auth-logo logo-light mx-auto"
                        />
                      </Link>
                    </div>
                    <h4 className="font-size-18 text-muted mt-2 text-center">
                      Welcome Back!
                    </h4>
                    <p className="mb-5 text-center">
                      Sign in to continue to Happilee Digital Innovations.
                    </p>
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {errorMessage && (
                        <Alert 
                          color="danger"
                          isOpen={!!errorMessage}
                          toggle={() => setErrorMessage("")}
                        >
                          <div className="text-center">
                            {errorMessage}
                          </div>
                        </Alert>
                      )}
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <Label className="form-label">Email</Label>
                            <Input
                              name="email"
                              className="form-control"
                              placeholder="Enter email"
                              type="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={
                                validation.touched.email &&
                                validation.errors.email
                              }
                            />
                            {validation.touched.email &&
                              validation.errors.email && (
                                <FormFeedback type="invalid">
                                  {validation.errors.email}
                                </FormFeedback>
                              )}
                          </div>
                          <div className="mb-4">
                            <Label className="form-label">Password</Label>
                            <Input
                              name="password"
                              className="form-control"
                              placeholder="Enter Password"
                              type="password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password || ""}
                              invalid={
                                validation.touched.password &&
                                validation.errors.password
                              }
                            />
                            {validation.touched.password &&
                              validation.errors.password && (
                                <FormFeedback type="invalid">
                                  {validation.errors.password}
                                </FormFeedback>
                              )}
                          </div>
                          <Row>
                            <Col className="text-end">
                              <div className="text-md-end mt-3 mt-md-0">
                                <Link
                                  to="/forgot-password"
                                  className="text-muted"
                                >
                                  <i className="mdi mdi-lock"></i> Forgot your
                                  password?
                                </Link>
                              </div>
                            </Col>
                          </Row>
                          <div className="d-grid mt-4">
                            <button
                              className="btn btn-primary waves-effect waves-light"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Logging In...
                                </>
                              ) : (
                                "Log In"
                              )}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                    <div className="text-center my-2 mb-4" style={{width:'100%'}}>
                      <button
                        type="button"
                        className="btn btn-outline-primary me-2"
                        onClick={() => navigate("/EmployeeLogin")}
                        style={{width:'100%'}}
                      >
                        Employee Login
                      </button>
                    </div>
                    <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 mb-4 text-center">
                      <Link to="/privacy-policy" className="text-muted">
                        Privacy Policy
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/terms-conditions" className="text-muted">
                        Terms & Conditions
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/contact-us" className="text-muted">
                        Contact Us
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-5 text-center">
                <p className="text-white-50">
                  Don't have an account?{" "}
                  <Link to="/register" className="fw-medium text-primary">
                    Register
                  </Link>
                </p>
                <p className="text-white-50">
                  © {new Date().getFullYear()} Crafted with{" "}
                  <i className="mdi mdi-heart text-danger"></i> by Govindani
                  Infotech Pvt. Ltd.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Login.propTypes = {
  error: PropTypes.string,
};

export default Login;